// ==================================
// File: src/pages/ContactPage.jsx
// DB-backed Contact Page (Production)
// ==================================

import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import { Footer } from "../components/Footer";
import api from "../utils/api";
import { Helmet } from "react-helmet";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PageContainer from "../components/PageContainer";
/* =======================
   Validation Schema
======================= */
const schema = yup.object({
  name: yup.string().trim().required("Your name is required"),
  email: yup
    .string()
    .email("Enter a valid email address")
    .required("Email is required"),
  message: yup
    .string()
    .trim()
    .min(5, "Message must be at least 5 characters")
    .required("Message is required"),

  // 🕳️ Honeypot (bots only)
  company: yup.string().notRequired(),
});

export default function ContactPage() {
  const [sending, setSending] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    type: "success",
  });

  const [successScreen, setSuccessScreen] = useState(false);
  const successTimerRef = useRef(null);

  /* =======================
     React Hook Form
  ======================= */
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  /* =======================
     Success Overlay Timer
  ======================= */
  useEffect(() => {
    if (successScreen) {
      successTimerRef.current = setTimeout(() => {
        setSuccessScreen(false);
      }, 3000);
    }

    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, [successScreen]);

  /* =======================
     Helpers
  ======================= */
  const showToast = (msg, type = "error") => {
    setSnack({ open: true, msg, type });
  };

  /* =======================
     Submit Handler
  ======================= */
  const onSubmit = async (data) => {
    try {
      setSending(true);

      // 🕳️ Honeypot trap (silent success)
      if (data.company) {
        showToast("Message sent successfully!", "success");
        reset();
        return;
      }

      await api.post("/contact/send", {
        name: data.name,
        email: data.email,
        message: data.message,
      });

      setSuccessScreen(true);
      reset();

      showToast("Message received. Our support team will respond shortly.", "success");
    } catch (err) {
      console.error("Contact form error:", err);
      showToast("Failed to send message. Please try again.", "error");
    } finally {
      setSending(false);
    }
  };

  return (
     <PageContainer>
    <Box sx={{ bgcolor: "#f7f9fc", minHeight: "100vh" }}>
      {/* ==================== SEO ===================== */}
      <Helmet>
        <title>Contact & Support - CBT Nova</title>
        <meta
          name="description"
          content="Contact CBT Nova for support, feedback, or technical assistance. You can send us a message or reach us directly via email."
        />
      </Helmet>

      <Navbar />

      {/* ==================== Success Overlay ===================== */}
      {successScreen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center" }} elevation={4}>
            <svg width="90" height="90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" fill="#e7f7ed" />
              <path
                d="M40 62 L55 75 L82 45"
                stroke="#2ea44f"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <Typography variant="h6" fontWeight={700}>
              Message Sent!
            </Typography>
            <Typography sx={{ mt: 1, color: "text.secondary" }}>
              Thank you for contacting CBT Nova. We will get back to you shortly.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* ==================== Main Content ===================== */}
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Contact & Support
          </Typography>

          <Typography sx={{ mt: 2 }}>
            Have a question, need help with your account, or want to report an issue?
            Please fill out the form below and our support team will assist you.
          </Typography>

          {/* ==================== FORM ===================== */}
          <Stack
            component="form"
            spacing={2}
            sx={{ mt: 4 }}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <TextField
              label="Your Name"
              fullWidth
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            <TextField
              label="Email Address"
              fullWidth
              type="email"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={4}
              {...register("message")}
              error={!!errors.message}
              helperText={errors.message?.message}
            />

            {/* 🕳️ Honeypot Field (Hidden) */}
            <input
              type="text"
              tabIndex="-1"
              autoComplete="off"
              style={{
                position: "absolute",
                left: "-9999px",
                height: 0,
                width: 0,
                opacity: 0,
              }}
              {...register("company")}
            />

            <Button
              variant="contained"
              size="large"
              fullWidth
              type="submit"
              disabled={sending}
              sx={{ py: 1.5, borderRadius: 2, textTransform: "none" }}
            >
              {sending ? "Sending..." : "Send Message"}
            </Button>
          </Stack>

          {/* ==================== Alternative Contact ===================== */}
          <Divider sx={{ my: 4 }} />

          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 3, bgcolor: "#fafafa" }}
          >
            <Typography fontWeight={700}>
              Other ways to contact us
            </Typography>

            <Typography sx={{ mt: 1 }}>
              You can also contact the CBT Nova support team directly via email:
            </Typography>

            <Typography sx={{ mt: 1, fontWeight: 600 }}>
              📧{" "}
              <a href="mailto:support@cbtnova.ng">
                support@cbtnova.ng
              </a>
            </Typography>

            <Typography sx={{ mt: 1 }} color="text.secondary">
              For faster assistance, please include your registered email
              address and a clear description of your issue.
            </Typography>
          </Paper>
        </Paper>
      </Container>

      <Footer />

      {/* ==================== Snackbar ===================== */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert variant="filled" severity={snack.type}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
	  </PageContainer>
  );
}
